import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import {
  catchError,
  finalize,
  firstValueFrom,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import type { AuthUser } from '../models/auth-user.model';
import type { ApiResponse } from '../models/api-response.model';
import type { StoredSession, TokenPair } from '../models/token-pair.model';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';

export interface LoginCredentials {
  email: string;
  password: string;
  totp_code?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly storage = inject(TokenStorageService);
  private readonly router = inject(Router);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));

  private readonly userState = signal<AuthUser | null>(null);
  private readonly accessTokenState = signal<string | null>(null);
  private readonly refreshTokenState = signal<string | null>(null);
  private readonly initializedState = signal(false);
  private refreshInFlight: Observable<TokenPair> | null = null;

  readonly user = this.userState.asReadonly();
  readonly accessToken = this.accessTokenState.asReadonly();
  readonly permissions = computed(() => this.userState()?.permissions ?? []);
  readonly departments = computed(() => this.userState()?.departments ?? []);
  readonly roles = computed(() => this.userState()?.roles ?? []);
  readonly isAuthenticated = computed(
    () => this.accessTokenState() !== null && this.userState() !== null,
  );
  readonly isInitialized = this.initializedState.asReadonly();

  /** Restores a stored session and loads the user profile. */
  initialize(): Promise<void> {
    const stored = this.storage.load();

    if (stored === null) {
      this.initializedState.set(true);
      return Promise.resolve();
    }

    this.applyStoredSession(stored);

    return firstValueFrom(
      this.loadMe().pipe(
        catchError(() => {
          this.clearSession();
          return of(null);
        }),
        finalize(() => this.initializedState.set(true)),
      ),
    ).then(() => undefined);
  }

  waitUntilInitialized(): Promise<void> {
    if (this.initializedState()) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.initializedState()) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  login(credentials: LoginCredentials): Observable<AuthUser> {
    return this.api.post<TokenPair>('auth/login', credentials).pipe(
      tap((tokens) => this.persistTokens(tokens)),
      tap(() => this.initializedState.set(true)),
      switchMap(() => this.loadMe()),
    );
  }

  loadMe(): Observable<AuthUser> {
    return this.api.get<AuthUser>('auth/me').pipe(tap((profile) => this.userState.set(profile)));
  }

  refresh(): Observable<TokenPair> {
    const refreshToken = this.refreshTokenState();

    if (refreshToken === null) {
      return throwError(() => new Error('No refresh token available'));
    }

    if (this.refreshInFlight !== null) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.rawHttp
      .post<ApiResponse<TokenPair>>(this.authUrl('auth/refresh'), {
        refresh_token: refreshToken,
      })
      .pipe(
        map((response) => {
          if (!response.success || response.data === null) {
            throw new Error(response.message || 'Token refresh failed');
          }

          return response.data;
        }),
        tap((tokens) => this.persistTokens(tokens)),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight = null;
        }),
      );

    return this.refreshInFlight;
  }

  logout(): Observable<void> {
    const refreshToken = this.refreshTokenState();

    const request$ =
      refreshToken !== null
        ? this.api.post<void>('auth/logout', { refresh_token: refreshToken }).pipe(
            catchError(() => of(undefined)),
            map(() => undefined),
          )
        : of(undefined);

    return request$.pipe(
      tap(() => this.clearSession()),
      tap(() => void this.router.navigate(['/login'])),
    );
  }

  hasPermission(code: string): boolean {
    return this.permissions().includes(code);
  }

  clearSession(): void {
    this.userState.set(null);
    this.accessTokenState.set(null);
    this.refreshTokenState.set(null);
    this.storage.clear();
  }

  private persistTokens(tokens: TokenPair): void {
    const expiresAt = Date.now() + tokens.expires_in * 1000;
    const session: StoredSession = {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      expires_at: expiresAt,
    };

    this.accessTokenState.set(tokens.access_token);
    this.refreshTokenState.set(tokens.refresh_token);
    this.storage.save(session);
  }

  private applyStoredSession(session: StoredSession): void {
    this.accessTokenState.set(session.access_token);
    this.refreshTokenState.set(session.refresh_token);
  }

  private authUrl(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const cleanPath = path.replace(/^\//, '');

    return `${base}/${cleanPath}`;
  }
}
